import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axiosInstance from '../api/axios';
// --- NEW IMPORTS ---
import logo from '../wysupp-icon-green-transparent.svg'; // Assuming logo is in the same folder or adjust path
import AccountInfo from '../AccountInfo'; // Assuming AccountInfo is in the same folder or adjust path
import { FaArrowLeft } from 'react-icons/fa'; // For back button

// --- Helper Functions for System Logs ---
const extractSystemLogType = (description) => {
  if (!description) return "Unknown";
  const rules = [
    { key: "Task Status Changed", text: "marked task" }, { key: "Interface Cancelled", text: "cancelled interface" },
    { key: "Interface Reopened", text: "reopened completed interface" }, { key: "Interface Completed", text: "marked interface" },
    { key: "Schedule Updated", text: "updated the schedule for project" }, { key: "Email Prefs Updated", text: "updated email preferences for project" },
    { key: "User Status Changed", text: "deactivated user" }, { key: "User Status Changed", text: "reactivated user" },
    { key: "Project Edited", text: "updated project" }, { key: "Project Created", text: "created project" },
    { key: "User Edited", text: "updated the user" }, { key: "User Deleted", text: "deleted the user" },
    { key: "User Created", text: "created with role" }, { key: "Account Locked", text: "locked due to too many failed" },
    { key: "Login Success", text: "logged in successfully" }, { key: "Logout", text: "revoked session for device" },
    { key: "Password Changed", text: "changed their password" }, { key: "Password Reset", text: "successfully reset their password" },
    { key: "Password Reset Request", text: "requested a password reset" }, { key: "Email Sent", text: "email sent to" }
  ];
  for (const rule of rules) { if (description.toLowerCase().includes(rule.text.toLowerCase())) { return rule.key; } }
  if (description.includes("Email notification sent to")) return "Email Notification Sent";
  return "System Event";
};

const extractSystemLogTimestampString = (description) => { /* Kept for exports */ };

const formatDate = (dateObj, includeTime = true) => {
  if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) { return "N/A"; }
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  if (includeTime) {
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
  return `${year}-${month}-${day}`;
};


export default function Monitoring() {
  const navigate = useNavigate();

  // --- State for System Logs ---
  const [systemLogs, setSystemLogs] = useState([]);
  const [systemLogsLoading, setSystemLogsLoading] = useState(true);
  const [systemLogsError, setSystemLogsError] = useState(null);
  const [sysUsernameFilter, setSysUsernameFilter] = useState('');
  const [sysLogTypeFilter, setSysLogTypeFilter] = useState('');
  const [sysStartDateFilter, setSysStartDateFilter] = useState('');
  const [sysEndDateFilter, setSysEndDateFilter] = useState('');

  // --- State for Interface Logs ---
  const [interfaceLogs, setInterfaceLogs] = useState([]);
  const [interfaceLogsLoading, setInterfaceLogsLoading] = useState(true);
  const [interfaceLogsError, setInterfaceLogsError] = useState(null);
  const [ifaceUsernameFilter, setIfaceUsernameFilter] = useState('');
  const [ifaceAlterationTypeFilter, setIfaceAlterationTypeFilter] = useState('');
  const [ifaceDidFailFilter, setIfaceDidFailFilter] = useState('');
  const [ifaceInterfaceFilter, setIfaceInterfaceFilter] = useState('');
  const [ifaceProjectFilter, setIfaceProjectFilter] = useState('');
  const [ifaceStartDateFilter, setIfaceStartDateFilter] = useState('');
  const [ifaceEndDateFilter, setIfaceEndDateFilter] = useState('');

  // --- Fetching Logic (Unchanged) ---
  const fetchSystemLogs = useCallback(async () => {
    setSystemLogsLoading(true); setSystemLogsError(null);
    try { const response = await axiosInstance.get('/logs/'); setSystemLogs(Array.isArray(response.data) ? response.data : []); }
    catch (err) { console.error("Error fetching system logs:", err); setSystemLogsError(err.response?.data?.detail || 'Failed to load system logs.'); }
    finally { setSystemLogsLoading(false); }
  }, []);
  const fetchInterfaceLogs = useCallback(async () => {
    setInterfaceLogsLoading(true); setInterfaceLogsError(null);
    try { const response = await axiosInstance.get('/interface-logs/'); setInterfaceLogs(Array.isArray(response.data) ? response.data : []); }
    catch (err) { console.error("Error fetching interface logs:", err); setInterfaceLogsError(err.response?.data?.detail || 'Failed to load interface logs.'); }
    finally { setInterfaceLogsLoading(false); }
  }, []);

  useEffect(() => { fetchSystemLogs(); fetchInterfaceLogs(); }, [fetchSystemLogs, fetchInterfaceLogs]);

  // --- Memoized Filters (Unchanged logic, adapted date check) ---
  const availableSystemLogTypes = useMemo(() => Array.from(new Set(systemLogs.map(log => extractSystemLogType(log.log_description)))).sort(), [systemLogs]);
  const filteredSystemLogs = useMemo(() => {
    return systemLogs.filter(log => {
      const matchesUsername = sysUsernameFilter ? (log.user_username || '').toLowerCase().includes(sysUsernameFilter.toLowerCase()) : true;
      const matchesLogType = sysLogTypeFilter ? extractSystemLogType(log.log_description) === sysLogTypeFilter : true;
      const logDate = log.timestamp ? new Date(log.timestamp) : null;
      let matchesDate = true;
      if (logDate && !isNaN(logDate)) {
        if (sysStartDateFilter) { const filterDate = new Date(sysStartDateFilter); filterDate.setUTCHours(0, 0, 0, 0); if (logDate < filterDate) matchesDate = false; }
        if (sysEndDateFilter && matchesDate) { const filterDate = new Date(sysEndDateFilter); filterDate.setUTCHours(23, 59, 59, 999); if (logDate > filterDate) matchesDate = false; }
      } else if (sysStartDateFilter || sysEndDateFilter) { matchesDate = false; }
      return matchesUsername && matchesLogType && matchesDate;
    });
  }, [systemLogs, sysUsernameFilter, sysLogTypeFilter, sysStartDateFilter, sysEndDateFilter]);

  const availableIfaceAlterationTypes = useMemo(() => Array.from(new Set(interfaceLogs.map(log => log.alteration_type).filter(Boolean))).sort(), [interfaceLogs]);
  const availableIfaceInterfaces = useMemo(() => Array.from(new Set(interfaceLogs.map(log => log.interface).filter(Boolean))).sort(), [interfaceLogs]);
  const availableIfaceProjects = useMemo(() => Array.from(new Set(interfaceLogs.map(log => log.project).filter(Boolean))).sort(), [interfaceLogs]);
  const filteredInterfaceLogs = useMemo(() => {
    return interfaceLogs.filter(log => {
      const matchesUsername = ifaceUsernameFilter ? (log.user_username || '').toLowerCase().includes(ifaceUsernameFilter.toLowerCase()) : true;
      const matchesAlterationType = ifaceAlterationTypeFilter ? log.alteration_type === ifaceAlterationTypeFilter : true;
      const matchesDidFail = ifaceDidFailFilter === '' ? true : String(log.didFail) === ifaceDidFailFilter;
      const matchesInterface = ifaceInterfaceFilter ? log.interface === ifaceInterfaceFilter : true;
      const matchesProject = ifaceProjectFilter ? log.project === ifaceProjectFilter : true;
      const logDate = log.timestamp ? new Date(log.timestamp) : null;
      let matchesDate = true;
      if (logDate && !isNaN(logDate)) {
        if (ifaceStartDateFilter) { const filterDate = new Date(ifaceStartDateFilter); filterDate.setUTCHours(0, 0, 0, 0); if (logDate < filterDate) matchesDate = false; }
        if (ifaceEndDateFilter && matchesDate) { const filterDate = new Date(ifaceEndDateFilter); filterDate.setUTCHours(23, 59, 59, 999); if (logDate > filterDate) matchesDate = false; }
      } else if (ifaceStartDateFilter || ifaceEndDateFilter) matchesDate = false;
      return matchesUsername && matchesAlterationType && matchesDidFail && matchesInterface && matchesProject && matchesDate;
    });
  }, [interfaceLogs, ifaceUsernameFilter, ifaceAlterationTypeFilter, ifaceDidFailFilter, ifaceInterfaceFilter, ifaceProjectFilter, ifaceStartDateFilter, ifaceEndDateFilter]);


  // --- Clear Filters (Unchanged) ---
  const handleClearSystemLogFilters = () => { setSysUsernameFilter(''); setSysLogTypeFilter(''); setSysStartDateFilter(''); setSysEndDateFilter(''); };
  const handleClearInterfaceLogFilters = () => { setIfaceUsernameFilter(''); setIfaceAlterationTypeFilter(''); setIfaceDidFailFilter(''); setIfaceInterfaceFilter(''); setIfaceProjectFilter(''); setIfaceStartDateFilter(''); setIfaceEndDateFilter(''); };

  // --- Scroll Function (Unchanged) ---
  const scrollToSystemLogs = () => { document.getElementById('system-logs-section')?.scrollIntoView({ behavior: 'smooth' }); };

  // --- Export Functions (Unchanged logic) ---
  const genericExportToCSV = (logs, filename, headers, rowMapper) => {
    if (logs.length === 0) { alert("No logs to export."); return; }
    const csvRows = [headers.join(','), ...logs.map(rowMapper)];
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' }); const link = document.createElement('a');
    link.href = URL.createObjectURL(blob); link.download = filename; link.style.visibility = 'hidden';
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };
  const genericExportToPDF = (logs, filename, title, tableColumns, rowMapper) => {
    if (logs.length === 0) { alert("No logs to export."); return; }
    const doc = new jsPDF(); doc.text(title, 14, 16);
    autoTable(doc, {
      head: [tableColumns.map(col => col.header)], body: logs.map(rowMapper), startY: 20, theme: 'striped',
      styles: { fontSize: 8, cellPadding: 1.5 }, headStyles: { fillColor: [45, 55, 72] }, // Dark header
      columnStyles: tableColumns.reduce((acc, col, index) => { if (col.width) acc[index] = { cellWidth: col.width }; return acc; }, {})
    });
    doc.save(filename);
  };
  // System Log Exporters (Adapted date formatting)
  const exportSystemLogsToCSV = () => genericExportToCSV(filteredSystemLogs, 'system_logs.csv', ["ID", "Username", "Action", "Timestamp", "Full Description"], log => [log.id, `"${log.user_username || ''}"`, `"${extractSystemLogType(log.log_description) || ''}"`, `"${formatDate(log.timestamp ? new Date(log.timestamp) : null)}"`, `"${(log.log_description || '').replace(/"/g, '""')}"`].join(','));
  const exportSystemLogsToPDF = () => genericExportToPDF(filteredSystemLogs, 'system_logs.pdf', "System Logs", [{ header: "ID", dataKey: "id", width: 10 }, { header: "Username", dataKey: "user_username", width: 30 }, { header: "Action", dataKey: "action", width: 40 }, { header: "Timestamp", dataKey: "timestamp_str", width: 40 }, { header: "Description", dataKey: "log_description" }], log => [log.id, log.user_username, extractSystemLogType(log.log_description), formatDate(log.timestamp ? new Date(log.timestamp) : null), log.log_description]);
  // Interface Log Exporters (Adapted date formatting)
  const exportInterfaceLogsToCSV = () => genericExportToCSV(filteredInterfaceLogs, 'interface_logs.csv', ["ID", "Username", "Alteration Type", "Failed", "Interface", "Project", "Timestamp", "Description"], log => [log.id, `"${log.user_username || ''}"`, `"${log.alteration_type || ''}"`, log.didFail, `"${log.interface || ''}"`, `"${log.project || ''}"`, `"${formatDate(log.timestamp ? new Date(log.timestamp) : null)}"`, `"${(log.log_description || '').replace(/"/g, '""')}"`].join(','));
  const exportInterfaceLogsToPDF = () => genericExportToPDF(filteredInterfaceLogs, 'interface_logs.pdf', "Interface Activity Logs", [{ header: "ID", dataKey: "id", width: 10 }, { header: "User", dataKey: "user_username", width: 25 }, { header: "Type", dataKey: "alteration_type", width: 25 }, { header: "Failed", dataKey: "didFail", width: 15 }, { header: "Interface", dataKey: "interface", width: 25 }, { header: "Project", dataKey: "project", width: 20 }, { header: "Timestamp", dataKey: "timestamp", width: 30 }, { header: "Description", dataKey: "log_description" }], log => [log.id, log.user_username, log.alteration_type, log.didFail ? 'Yes' : 'No', log.interface, log.project, formatDate(log.timestamp ? new Date(log.timestamp) : null), log.log_description]);

  return (
    // --- UPDATED: Use admin-page-container ---
    <div className="admin-page-container">
      {/* --- UPDATED: Use Header from ManageProjects --- */}
      <header className="dashboard-header">
        <Link to="/home" style={{ textDecoration: 'none' }}>
          <div className="logo-section" style={{ cursor: 'pointer' }}>
            <img src={logo} className="logo-icon" alt="Wysupp Logo" />
            <span className="logo-text">Wysupp Validate</span>
            <span className="logo-subtext">Data Quality Management</span>
          </div>
        </Link>
        <AccountInfo />
      </header>

      {/* --- UPDATED: Use admin-main-content --- */}
      <main className="admin-main-content">
        {/* --- NEW: Page Header --- */}
        <div className="page-header">
          <button onClick={() => navigate('/home')} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <h1 className="page-title">Log Monitoring</h1>
        </div>

        {/* --- Interface Logs Section (Adapted Styling) --- */}
        <div id="interface-logs-section" className="logs-container content-card"> {/* Added content-card */}
          <div className="section-header-controls">
            <h2 className="section-title">Interface Activity Logs</h2> {/* Changed h1 to h2 */}
            <button onClick={scrollToSystemLogs} className="scroll-to-button action-button secondary small"> {/* Use action button style */}
              Go to System Logs &#x21E9;
            </button>
          </div>
          {/* Filters */}
          <div className="filters-section">
            {/* --- UPDATED GRID CLASS --- */}
            <div className="filter-grid interface-log-filters">
              <div className="filter-group">
                <label htmlFor="ifaceUsernameFilter">Username</label>
                <input type="text" id="ifaceUsernameFilter" value={ifaceUsernameFilter} onChange={(e) => setIfaceUsernameFilter(e.target.value)} placeholder="Contains..." className="filter-input" />
              </div>
              <div className="filter-group">
                <label htmlFor="ifaceAlterationTypeFilter">Alteration Type</label>
                <select id="ifaceAlterationTypeFilter" value={ifaceAlterationTypeFilter} onChange={(e) => setIfaceAlterationTypeFilter(e.target.value)} className="filter-select">
                  <option value="">All Types</option>
                  {availableIfaceAlterationTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="ifaceDidFailFilter">Status</label>
                <select id="ifaceDidFailFilter" value={ifaceDidFailFilter} onChange={(e) => setIfaceDidFailFilter(e.target.value)} className="filter-select">
                  <option value="">All</option>
                  <option value="true">Failed</option>
                  <option value="false">Success</option>
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="ifaceInterfaceFilter">Interface</label>
                <select id="ifaceInterfaceFilter" value={ifaceInterfaceFilter} onChange={(e) => setIfaceInterfaceFilter(e.target.value)} className="filter-select">
                  <option value="">All Interfaces</option>
                  {availableIfaceInterfaces.map(item => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="ifaceProjectFilter">Project</label>
                <select id="ifaceProjectFilter" value={ifaceProjectFilter} onChange={(e) => setIfaceProjectFilter(e.target.value)} className="filter-select">
                  <option value="">All Projects</option>
                  {availableIfaceProjects.map(item => <option key={item} value={item}>{item}</option>)}
                </select>
              </div>
              {/* --- MOVED Date and Clear Button to new row --- */}
              <div className="filter-group date-filter-group"> {/* Added class */}
                <label htmlFor="ifaceStartDateFilter">Start Date</label>
                <input type="date" id="ifaceStartDateFilter" value={ifaceStartDateFilter} onChange={(e) => setIfaceStartDateFilter(e.target.value)} className="filter-input" />
              </div>
              <div className="filter-group date-filter-group"> {/* Added class */}
                <label htmlFor="ifaceEndDateFilter">End Date</label>
                <input type="date" id="ifaceEndDateFilter" value={ifaceEndDateFilter} onChange={(e) => setIfaceEndDateFilter(e.target.value)} className="filter-input" />
              </div>
              <div className="filter-group clear-filters-container">
                <button onClick={handleClearInterfaceLogFilters} className="clear-filters-button action-button danger small">Clear</button> {/* Use action button */}
              </div>
            </div>
          </div>
          {/* Export Buttons */}
          <div className="export-buttons-section">
            <button onClick={exportInterfaceLogsToCSV} className="export-button action-button secondary">Export CSV</button> {/* Use action button */}
            <button onClick={exportInterfaceLogsToPDF} className="export-button action-button secondary">Export PDF</button> {/* Use action button */}
          </div>
          {/* Table */}
          {interfaceLogsLoading && <div className="loading-indicator"><div className="spinner"></div><p>Loading interface logs...</p></div>}
          {!interfaceLogsLoading && interfaceLogsError && <div className="error-message"><p><strong>Error:</strong> {interfaceLogsError}</p><button onClick={() => fetchInterfaceLogs()} className="retry-button action-button danger small">Retry</button></div>}
          {!interfaceLogsLoading && !interfaceLogsError && (
            <>
              <div className="logs-table-container">
                <table className="logs-table styled-table"> {/* Added styled-table */}
                  <thead>
                    <tr>
                      <th>ID</th><th>User</th><th>Timestamp</th><th>Type</th>
                      <th>Interface</th><th>Project</th><th>Status</th><th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInterfaceLogs.length > 0 ? filteredInterfaceLogs.map(log => (
                      <tr key={log.id}>
                        <td>{log.id}</td><td>{log.user_username}</td><td>{formatDate(log.timestamp ? new Date(log.timestamp) : null)}</td>
                        <td>{log.alteration_type}</td><td>{log.interface}</td><td>{log.project}</td>
                        <td>{log.didFail ? <span className="status-badge status-failed">Failed</span> : <span className="status-badge status-success">Success</span>}</td> {/* Use styled badges */}
                        <td className="description-cell">{log.log_description}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="8" className="no-logs-message">No interface logs found matching filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="logs-count-message">Showing {filteredInterfaceLogs.length} of {interfaceLogs.length} interface logs.</div>
            </>
          )}
        </div>

        {/* --- System Logs Section (Adapted Styling) --- */}
        <div id="system-logs-section" className="logs-container content-card"> {/* Added content-card */}
          <h2 className="section-title">System Event Logs</h2> {/* Changed h1 to h2 */}
          {/* Filters */}
          <div className="filters-section">
            {/* --- UPDATED GRID CLASS --- */}
            <div className="filter-grid system-log-filters">
              <div className="filter-group">
                <label htmlFor="sysUsernameFilter">Username</label>
                <input type="text" id="sysUsernameFilter" value={sysUsernameFilter} onChange={(e) => setSysUsernameFilter(e.target.value)} placeholder="Contains..." className="filter-input" />
              </div>
              <div className="filter-group">
                <label htmlFor="sysLogTypeFilter">Log Type</label>
                <select id="sysLogTypeFilter" value={sysLogTypeFilter} onChange={(e) => setSysLogTypeFilter(e.target.value)} className="filter-select">
                  <option value="">All Types</option>
                  {availableSystemLogTypes.map(type => <option key={type} value={type}>{type}</option>)}
                </select>
              </div>
              {/* --- MOVED Date and Clear Button --- */}
              <div className="filter-group date-filter-group"> {/* Added class */}
                <label htmlFor="sysStartDateFilter">Start Date</label>
                <input type="date" id="sysStartDateFilter" value={sysStartDateFilter} onChange={(e) => setSysStartDateFilter(e.target.value)} className="filter-input" />
              </div>
              <div className="filter-group date-filter-group"> {/* Added class */}
                <label htmlFor="sysEndDateFilter">End Date</label>
                <input type="date" id="sysEndDateFilter" value={sysEndDateFilter} onChange={(e) => setSysEndDateFilter(e.target.value)} className="filter-input" />
              </div>
              <div className="filter-group clear-filters-container">
                <button onClick={handleClearSystemLogFilters} className="clear-filters-button action-button danger small">Clear</button> {/* Use action button */}
              </div>
            </div>
          </div>
          {/* Export Buttons */}
          <div className="export-buttons-section">
            <button onClick={exportSystemLogsToCSV} className="export-button action-button secondary">Export CSV</button> {/* Use action button */}
            <button onClick={exportSystemLogsToPDF} className="export-button action-button secondary">Export PDF</button> {/* Use action button */}
          </div>
          {/* Table */}
          {systemLogsLoading && <div className="loading-indicator"><div className="spinner"></div><p>Loading system logs...</p></div>}
          {!systemLogsLoading && systemLogsError && <div className="error-message"><p><strong>Error:</strong> {systemLogsError}</p><button onClick={() => fetchSystemLogs()} className="retry-button action-button danger small">Retry</button></div>}
          {!systemLogsLoading && !systemLogsError && (
            <>
              <div className="logs-table-container">
                <table className="logs-table styled-table"> {/* Added styled-table */}
                  <thead>
                    <tr>
                      <th>ID</th><th>Username</th><th>Action</th><th>Timestamp</th><th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSystemLogs.length > 0 ? filteredSystemLogs.map(log => (
                      <tr key={log.id}>
                        <td>{log.id}</td><td>{log.user_username}</td><td className="action-cell">{extractSystemLogType(log.log_description)}</td>
                        <td>{formatDate(log.timestamp ? new Date(log.timestamp) : null)}</td>
                        <td className="description-cell">{log.log_description}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="5" className="no-logs-message">No system logs found matching filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="logs-count-message">Showing {filteredSystemLogs.length} of {systemLogs.length} system logs.</div>
            </>
          )}
        </div>
      </main>

      {/* --- UPDATED: Dark Theme Styles --- */}
      <style>{`
        /* Dark Theme Variables */
        :root {
            --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
            --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
            --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
            --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1); --success-border: #34d399;
        }
        body { background-color: var(--dark-bg); } /* Ensure body bg is dark */

        /* Page Layout */
        .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
        .admin-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }

        /* Header from ManageProjects */
        .dashboard-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 3rem; background-color: var(--card-bg); border-bottom: 1px solid var(--border-color); }
        .logo-section { display: flex; align-items: center; gap: 0.5rem; }
        .logo-icon { width: 30px; height: 30px; }
        .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
        .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); }
        .dashboard-header > div:last-child { flex-shrink: 0; } /* AccountInfo */

        /* Page Header (with Back Button) */
        .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
        .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
        .back-button:hover { background: #4a5568; }
        .page-title { font-size: 2rem; font-weight: 700; color: var(--text-color); margin: 0; }

        /* Content Card Style */
        .content-card { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 12px; padding: 1.5rem 2rem; box-shadow: 0 4px 6px rgba(0,0,0,0.1); margin-bottom: 2rem; }

        /* Section Header within Cards */
        .section-title { font-size: 1.5rem; font-weight: 600; color: var(--text-color); margin-bottom: 1.5rem; border-bottom: 1px solid var(--border-color); padding-bottom: 1rem; }
        .section-header-controls { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; padding-bottom: 1.5rem; border-bottom: 1px solid var(--border-color); }
        .section-header-controls .section-title { margin-bottom: 0; border-bottom: none; padding-bottom: 0; }

        /* --- UPDATED Filters --- */
        .filters-section { margin-bottom: 2rem; } /* Increased bottom margin */
        .filter-grid {
            display: grid;
            gap: 1.25rem 1.5rem; /* Row gap, Column gap */
            align-items: end; /* Align items to the bottom of their cell */
        }
        /* System Logs: 2 text filters, 2 date filters, 1 clear button = 5 items */
        .system-log-filters {
            grid-template-columns: repeat(5, 1fr); /* Try 5 equal columns */
        }
        /* Interface Logs: 5 selects, 2 date filters, 1 clear button = 8 items */
        .interface-log-filters {
            grid-template-columns: repeat(5, 1fr); /* 5 columns for the selects */
        }
        .interface-log-filters .date-filter-group {
            /* Explicitly place date filters if needed, e.g., for specific start column */
            /* grid-column: span 1; */
        }
         .interface-log-filters .clear-filters-container {
            grid-column: 5 / 6; /* Place clear button in the last column of the second row */
        }

        .filter-group { display: flex; flex-direction: column; }
        .filter-group label { font-size: 0.875rem; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }
        .filter-input, .filter-select {
            width: 100%; /* Make inputs fill their grid cell */
            padding: 0.6rem 0.8rem; border: 1px solid var(--border-color); border-radius: 6px; font-size: 0.9rem; background-color: var(--input-bg); color: var(--text-color); color-scheme: dark; box-sizing: border-box; /* Include padding in width */
        }
        .filter-input::placeholder { color: var(--light-text); opacity: 0.7; }
        .filter-input:focus, .filter-select:focus { outline: none; border-color: var(--green-accent); box-shadow: 0 0 0 3px rgba(72, 187, 120, 0.3); }
        .filter-select option { background-color: var(--card-bg); color: var(--text-color); }
        .clear-filters-container { justify-content: flex-end; /* Keep button right-aligned within its cell */ }
        .clear-filters-button { width: auto; /* Allow button to size naturally */ }


        /* Buttons */
        .action-button { padding: 0.6rem 1.2rem; border: none; border-radius: 6px; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; gap: 0.5rem; line-height: 1.2; }
        .action-button.secondary { background-color: var(--border-color); color: var(--text-color); }
        .action-button.secondary:hover { background-color: #5a6578; }
        .action-button.danger { background-color: var(--danger-color); color: var(--text-color); }
        .action-button.danger:hover { background-color: #dc2626; }
        .action-button.small { padding: 0.4rem 0.8rem; font-size: 0.8rem; } /* Smaller button variant */
        .export-buttons-section { display: flex; gap: 1rem; margin-bottom: 1.5rem; border-top: 1px solid var(--border-color); padding-top: 1.5rem; } /* Add separator */
        .export-button {} /* Now uses .action-button */
        .clear-filters-button {} /* Now uses .action-button */
        .scroll-to-button {} /* Now uses .action-button */

        /* Table Styles */
        .logs-table-container { overflow-x: auto; margin-bottom: 1rem; }
        .styled-table { width: 100%; border-collapse: collapse; background-color: var(--card-bg); /* Table background */ border: 1px solid var(--border-color); border-radius: 8px; overflow: hidden; }
        .styled-table th, .styled-table td { padding: 0.8rem 1rem; text-align: left; border-bottom: 1px solid var(--border-color); }
        .styled-table th { background-color: var(--input-bg); /* Darker header */ font-size: 0.75rem; text-transform: uppercase; color: var(--light-text); font-weight: 600; white-space: nowrap; }
        .styled-table tbody tr { transition: background-color 0.15s ease-in-out; }
        .styled-table tbody tr:hover { background-color: #374151; /* Slightly lighter on hover */ }
        .styled-table tbody tr:last-child td { border-bottom: none; }
        .styled-table td { color: var(--text-color); font-size: 0.9rem; vertical-align: middle; }
        .description-cell { max-width: 450px; white-space: normal; word-break: break-word; font-size: 0.85rem; color: var(--light-text); }
        .action-cell { font-weight: 500; }
        .no-logs-message { text-align: center; color: var(--light-text); font-style: italic; padding: 1.5rem; }

        /* Status Badges */
        .status-badge { display: inline-block; padding: 0.2rem 0.6rem; border-radius: 9999px; font-size: 0.8rem; font-weight: 600; line-height: 1.2; }
        .status-failed { background-color: var(--error-bg); color: var(--error-color); border: 1px solid var(--danger-color); }
        .status-success { background-color: var(--success-bg); color: var(--success-color); border: 1px solid var(--success-border); }

        /* Messages & Loaders */
        .loading-indicator { display: flex; align-items: center; justify-content: center; padding: 3rem; color: var(--light-text); }
        .spinner { width: 24px; height: 24px; border: 3px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; margin-right: 1rem; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .error-message { background-color: var(--error-bg); color: var(--error-color); padding: 1rem; border: 1px solid var(--danger-color); border-radius: 8px; margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; }
        .retry-button {} /* Now uses action-button */
        .logs-count-message { text-align: right; margin-top: 1rem; font-size: 0.9rem; color: var(--light-text); }

        /* Responsive */
        @media (max-width: 1200px) { /* Adjust breakpoint if needed */
             .system-log-filters { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
             .interface-log-filters { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); }
             .interface-log-filters .clear-filters-container { grid-column: auto; } /* Reset column span */
        }
        @media (max-width: 768px) {
            .admin-main-content { padding: 0 1rem; }
            .dashboard-header { flex-direction: column; align-items: stretch; padding: 1rem; }
            .logo-section { justify-content: center; }
            .page-header { flex-wrap: wrap; }
            .page-title { flex-basis: 100%; order: 2; text-align: center; margin-top: 0.5rem; font-size: 1.6rem; }
            .back-button { order: 1; }
            .filter-grid { grid-template-columns: 1fr; } /* Stack filters fully on small screens */
            .clear-filters-container { align-items: stretch; }
            .clear-filters-button { width: 100%; }
            .export-buttons-section { flex-direction: column; gap: 0.5rem; align-items: stretch; padding-top: 1rem; } /* Adjust padding */
            .export-button { width: 100%; }
            .styled-table th, .styled-table td { padding: 0.6rem 0.8rem; font-size: 0.85rem; }
            .description-cell { max-width: 250px; }
        }
      `}</style>
    </div>
  );
}


